/* Identity — Users / Roles. Roles are the access map: granting a journey to a
 * role IS publishing (menu ≡ gate). Every list + mutation runs a CONFIG-plane
 * workflow — the platform never touches the execute plane. FULLY VISUAL: a
 * users table + role cards (journey chips wrap, so it scales to any number of
 * journeys), edited through visual forms. No JSON, no copy-pack. */
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    useSearchParams
} from 'react-router-dom';
import {
    Pencil,
    Plus,
    RefreshCw,
    Trash2,
    Users
} from 'lucide-react';
import {
    appEngineService
} from '../../api/appEngine.service';
import {
    useAppStudio
} from '../../AppStudioContext';
import IdentityEditDialog from './IdentityEditDialog';
import type {
    DefListItem,
    IdentityDoc
} from '../../api/types';

const TABS = ['users', 'roles'] as const;
/** Structural roles (locked) — undeletable; delete is disabled for them. */
const STRUCTURAL_ROLES = new Set(['anonymous', 'admin']);

function RoleChips({
    values
}: {
    values: any;
}) {
    const list = Array.isArray(values) ? values : [];
    if (list.length === 0) return <span className="aps-muted">—</span>;
    return (
        <span className="aps-chip-row">
            {list.map((v, i) => (
                <span key={i} className="aps-badge aps-badge-indigo">{String(v)}</span>
            ))}
        </span>
    );
}

export default function IdentityPage() {
    const {
        tenancy
    } = useAppStudio();
    // Deep-linkable tab: the Build journey's Access stage lands on ?tab=roles
    // (legacy ?tab=access maps to Roles too).
    const [search] = useSearchParams();
    const urlTab = search.get('tab');
    const [tab, setTab] = useState<string>(
        urlTab === 'roles' || urlTab === 'access' ? 'roles' : 'users');
    const [docs, setDocs] = useState<IdentityDoc[]>([]);
    const [loading, setLoading] = useState(true);
    // Roles + journeys feed the dialog's multi-selects and the role-card chips.
    const [allRoles, setAllRoles] = useState<IdentityDoc[]>([]);
    const [journeys, setJourneys] = useState<DefListItem[]>([]);
    const [editing, setEditing] = useState<IdentityDoc | null>(null);
    const [intent, setIntent] = useState<'delete' | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rows, roles, jrns] = await Promise.all([
                tab === 'users'
                    ? appEngineService.listUsers(tenancy)
                    : appEngineService.listRoles(tenancy),
                appEngineService.listRoles(tenancy),
                appEngineService.listDefs(tenancy, 'journey').catch(() => [])
            ]);
            setDocs(rows);
            setAllRoles(roles);
            setJourneys(jrns);
        } catch {
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, [tenancy, tab]);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = (doc: IdentityDoc | null, act: 'delete' | null = null) => {
        setEditing(doc);
        setIntent(act);
        setDialogOpen(true);
    };

    // The ONLY user holding 'admin' — its Delete is disabled (deleting it would
    // leave nobody able to manage roles; wf_delete_user blocks it server-side too).
    const soleAdminUserId = useMemo(() => {
        if (tab !== 'users') return null;
        const holders = docs.filter((d) => Array.isArray(d.roles)
            && d.roles.includes('admin'));
        return holders.length === 1 ? holders[0].id || null : null;
    }, [tab, docs]);

    const roleOptions = useMemo(() => allRoles.map((r) => ({
        value: String(r.id || ''),
        label: r.name ? `${r.name}` : String(r.id || '')
    })).filter((o) => o.value), [allRoles]);

    const journeyOptions = useMemo(() => journeys.map((j) => ({
        value: String(j.id || ''),
        label: j.name ? `${j.name}` : String(j.id || '')
    })).filter((o) => o.value), [journeys]);

    // Friendly journey name for a granted-journey chip (falls back to the id).
    const journeyLabel = useCallback((id: string) => {
        const o = journeyOptions.find((opt) => opt.value === id);
        return o ? o.label : id;
    }, [journeyOptions]);

    return (
        <div className="aps-page">
            <div className="aps-page-narrow">
                <div className="aps-page-head">
                    <div className="aps-page-head-text">
                        <h1 className="aps-h2">Identity</h1>
                        <p className="aps-page-sub">
                            Data in the predefined identity source — roles are the access
                            map; adding a journey to a role publishes it.
                        </p>
                    </div>
                    <button className="aps-icon-btn" title="Refresh" onClick={load}>
                        <RefreshCw size={15} className={loading ? 'aps-spin' : undefined} />
                    </button>
                    <button className="aps-btn aps-btn-primary aps-btn-sm" onClick={() => openEdit(null)}>
                        <Plus size={14} />
                        New {tab.replace(/s$/, '')}
                    </button>
                </div>
                <div className="aps-chip-row" style={{
                    marginBottom: 16
                }}>
                    {TABS.map((t) => (
                        <button key={t}
                            className={'aps-filter-chip' + (tab === t ? ' active' : '')}
                            onClick={() => {
                                if (t === tab) return;
                                // Clear immediately so the OTHER tab's rows never
                                // flash in this tab's layout while loading.
                                setDocs([]);
                                setLoading(true);
                                setTab(t);
                            }}>
                            {t}
                        </button>
                    ))}
                </div>

                {loading && docs.length === 0 ? (
                    <div className="aps-empty" style={{
                        padding: '36px 16px'
                    }}>
                        <RefreshCw size={18} className="aps-spin" />
                        <span>Loading {tab}…</span>
                    </div>
                ) : docs.length === 0 ? (
                    <div className="aps-empty" style={{
                        padding: '40px 16px'
                    }}>
                        <span className="aps-empty-icon"><Users size={20} /></span>
                        <span>No {tab} yet — use “New {tab.replace(/s$/, '')}”.</span>
                    </div>
                ) : tab === 'users' ? (
                    <div className="aps-table-wrap">
                        <table className="aps-table">
                            <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>Email</th>
                                    <th>Name</th>
                                    <th>Roles</th>
                                    <th style={{
                                        textAlign: 'right'
                                    }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {docs.map((d, i) => {
                                    const sole = !!d.id && d.id === soleAdminUserId;
                                    return (
                                        <tr key={d.id || i}>
                                            <td className="aps-mono">{d.id || '—'}</td>
                                            <td>{d.email || '—'}</td>
                                            <td>{d.name || '—'}</td>
                                            <td><RoleChips values={d.roles} /></td>
                                            <td>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: 4,
                                                    justifyContent: 'flex-end'
                                                }}>
                                                    <button className="aps-icon-btn" title="Edit"
                                                        onClick={() => openEdit(d)}>
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button className="aps-icon-btn"
                                                        title={sole
                                                            ? 'Sole admin — grant admin to another user first'
                                                            : 'Delete'}
                                                        disabled={sole}
                                                        style={{
                                                            color: sole ? undefined : 'var(--aps-danger)'
                                                        }}
                                                        onClick={() => openEdit(d, 'delete')}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: 12
                    }}>
                        {docs.map((d, i) => {
                            const structural = STRUCTURAL_ROLES.has(String(d.id || ''));
                            const jrns = Array.isArray(d.journeys)
                                ? d.journeys.map(String) : [];
                            return (
                                <div key={d.id || i} className="aps-card" style={{
                                    padding: 14,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 8
                                    }}>
                                        <div style={{
                                            minWidth: 0
                                        }}>
                                            <div style={{
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                flexWrap: 'wrap'
                                            }}>
                                                {d.name || d.id}
                                                {structural && (
                                                    <span className="aps-badge aps-badge-neutral">
                                                        structural
                                                    </span>
                                                )}
                                            </div>
                                            <div className="aps-mono aps-muted" style={{
                                                fontSize: 12,
                                                marginTop: 2
                                            }}>
                                                {d.id}
                                            </div>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: 4,
                                            flexShrink: 0
                                        }}>
                                            <button className="aps-icon-btn" title="Edit"
                                                onClick={() => openEdit(d)}>
                                                <Pencil size={14} />
                                            </button>
                                            <button className="aps-icon-btn"
                                                title={structural
                                                    ? 'Structural role — cannot be deleted'
                                                    : 'Delete'}
                                                disabled={structural}
                                                style={{
                                                    color: structural ? undefined : 'var(--aps-danger)'
                                                }}
                                                onClick={() => openEdit(d, 'delete')}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="aps-muted" style={{
                                            fontSize: 12,
                                            marginBottom: 6,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em'
                                        }}>
                                            Can open ({jrns.length})
                                        </div>
                                        {jrns.length === 0 ? (
                                            <span className="aps-muted">
                                                No journeys — this role sees nothing.
                                            </span>
                                        ) : (
                                            <span className="aps-chip-row">
                                                {jrns.map((jid) => (
                                                    <span key={jid}
                                                        className="aps-badge aps-badge-indigo"
                                                        title={jid}>
                                                        {journeyLabel(jid)}
                                                    </span>
                                                ))}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <IdentityEditDialog
                collection={tab}
                doc={editing}
                intent={intent}
                open={dialogOpen}
                roleOptions={roleOptions}
                journeyOptions={journeyOptions}
                soleAdminUserId={soleAdminUserId}
                onOpenChange={setDialogOpen}
                onDeleted={(id) => setDocs((prev) =>
                    prev.filter((d) => String(d.id) !== String(id)))}
                onUpserted={(doc) => setDocs((prev) => {
                    const id = String(doc.id || '');
                    const idx = prev.findIndex((d) => String(d.id) === id);
                    if (idx < 0) return [...prev, doc];
                    const next = prev.slice();
                    next[idx] = { ...next[idx], ...doc };
                    return next;
                })}
                onChanged={load}
            />
        </div>
    );
}
